import React, { useState } from "react";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  setDoc,
  updateDoc
} from "firebase/firestore";
import { db } from "../../firebase";
import { useFamilyData } from "../../hooks/useFamilyData";
import { logAudit } from "../../utils/audit";
import { createActionId, executeAction } from "../../utils/actions";
import { runActionWithQueue } from "../../utils/actionQueue";
import { useToast } from "../../components/ui/ToastProvider";
import { useConfirm } from "../../components/ui/ConfirmProvider";

export default function RewardsCard({ familyId, isPro }) {
  const { showToast } = useToast();
  const { confirm } = useConfirm();
  const { rewards = [], redemptions = [] } = useFamilyData(familyId);
  const [rewardName, setRewardName] = useState("");
  const [rewardCost, setRewardCost] = useState(0);

  const addReward = async () => {
    if (!rewardName) return;
    await addDoc(collection(db, "families", familyId, "rewards"), {
      name: rewardName,
      cost: Number(rewardCost) || 0,
      active: true,
      createdAt: new Date()
    });
    setRewardName("");
    setRewardCost(0);
    await logAudit({ familyId, action: "create", entity: "reward", meta: { name: rewardName } });
    showToast({ type: "success", message: "Reward added." });
  };

  const toggleReward = async (reward) => {
    await updateDoc(doc(db, "families", familyId, "rewards", reward.id), { active: !reward.active });
    await logAudit({ familyId, action: "toggle", entity: "reward", meta: { rewardId: reward.id } });
  };

  const removeReward = async (rewardId) => {
    const reward = rewards.find((entry) => entry.id === rewardId);
    const proceed = await confirm({
      title: "Remove reward?",
      message: `Remove "${reward?.name || "this reward"}" from the shop?`,
      confirmLabel: "Remove",
      cancelLabel: "Cancel",
      danger: true
    });
    if (!proceed) return;
    const snapshot = reward ? { ...reward } : null;
    await deleteDoc(doc(db, "families", familyId, "rewards", rewardId));
    await logAudit({ familyId, action: "delete", entity: "reward", meta: { rewardId } });
    showToast({
      message: "Reward removed.",
      actionLabel: snapshot ? "Undo" : null,
      onAction: snapshot
        ? async () => {
            await setDoc(doc(db, "families", familyId, "rewards", rewardId), snapshot, { merge: true });
            await logAudit({ familyId, action: "restore", entity: "reward", meta: { rewardId } });
          }
        : null
    });
  };

  const approveRedemption = async (redemption) => {
    const actionId = createActionId();
    const { queued } = await runActionWithQueue(
      { type: "redemption.approve", familyId, redemptionId: redemption.id, actionId },
      executeAction
    );
    if (!queued) {
      await logAudit({ familyId, action: "approve", entity: "redemption", meta: { redemptionId: redemption.id } });
    }
  };

  const denyRedemption = async (redemption) => {
    const actionId = createActionId();
    const { queued } = await runActionWithQueue(
      { type: "redemption.deny", familyId, redemptionId: redemption.id, actionId },
      executeAction
    );
    if (!queued) {
      await logAudit({ familyId, action: "deny", entity: "redemption", meta: { redemptionId: redemption.id } });
    }
  };

  const pendingRedemptions = redemptions.filter(r => r.status === "pending");

  return (
    <div className="glass parent-card">
      <div className="header-flex">
        <h2>Reward Shop</h2>
        {pendingRedemptions.length > 0 && <span className="badge">{pendingRedemptions.length} Pending</span>}
      </div>
      <div className="card-body">
        {!isPro && (
          <p style={{ textAlign: "center", opacity: 0.6 }}>Upgrade to Pro to enable the reward shop.</p>
        )}
        {isPro && (
        <div style={{ display: "flex", gap: "8px", marginBottom: "10px" }}>
          <input
            placeholder="Reward name"
            value={rewardName}
            onChange={(e) => setRewardName(e.target.value)}
          />
          <input
            type="number"
            placeholder="Cost"
            value={rewardCost}
            onChange={(e) => setRewardCost(e.target.value)}
            style={{ width: "90px" }}
          />
          <button className="btn-nav success-btn" onClick={addReward}>Add</button>
        </div>
        )}

        {isPro && rewards.length === 0 && (
          <div style={{ opacity: 0.7, textAlign: "center", display: "grid", gap: 8 }}>
            <p style={{ margin: 0 }}>No rewards yet.</p>
            <button className="btn-nav" type="button" onClick={() => document.querySelector("input[placeholder='Reward name']")?.focus()}>
              Add your first reward
            </button>
          </div>
        )}
        {isPro && rewards.map(r => (
          <div key={r.id} className="parent-list-item" style={{ gap: "8px" }}>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <strong>{r.name}</strong>
              <small style={{ opacity: 0.7 }}>⭐ {r.cost || 0}</small>
            </div>
            <div style={{ display: "flex", gap: "6px" }}>
              <button className="btn-nav" onClick={() => toggleReward(r)}>
                {r.active ? "Active" : "Paused"}
              </button>
              <button className="btn-nav" style={{ color: "var(--danger)" }} onClick={() => removeReward(r.id)}>
                Remove
              </button>
            </div>
          </div>
        ))}

        {isPro && pendingRedemptions.length > 0 && (
          <>
            <h3 style={{ marginTop: "15px" }}>Pending Redemptions</h3>
            {pendingRedemptions.map(r => (
              <div key={r.id} className="card">
                <div style={{ marginBottom: "6px" }}>
                  <strong>{r.rewardName}</strong> - ⭐ {r.cost || 0}
                  <div style={{ fontSize: "0.85rem", opacity: 0.8 }}>Requested by {r.kidName || "Unknown"}</div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                  <button className="btn-nav success-btn" onClick={() => approveRedemption(r)}>Approve</button>
                  <button className="btn-nav" style={{ color: "var(--danger)" }} onClick={() => denyRedemption(r)}>Deny</button>
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}
